import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Truck, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ALL_SERVICES, BUSINESS } from "@/data/services";
import { submitBooking } from "@/lib/api";
import AvailabilityPicker from "@/components/AvailabilityPicker";

const CUSTOM_OPTION = "Custom Estimate (multi-service)";

const inputCls =
  "w-full bg-carbon border border-edge rounded-lg px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt focus:ring-1 focus:ring-volt/40 transition-colors";

const BookingContactSection = ({ prefill, onPrefillConsumed }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", date: "", time: "", address: "", payment: "", notes: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({ ...f, service: prefill.service, notes: prefill.notes }));
      onPrefillConsumed();
    }
  }, [prefill, onPrefillConsumed]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please select a service.");
      return;
    }
    if (!form.date || !form.time) {
      toast.error("Please pick a date and time slot from the calendar.");
      return;
    }
    if (!form.address.trim()) {
      toast.error("Please enter the address where we should come.");
      return;
    }
    if (!form.payment) {
      toast.error("Please choose a payment method — cash or bank transfer.");
      return;
    }
    setSending(true);
    try {
      const payload = { ...form };
      if (!payload.email.trim()) delete payload.email;
      const res = await submitBooking(payload);
      toast.success("Booking request sent.", {
        description: "We'll confirm your slot shortly by phone or email.",
      });
      if (res.email === "failed") {
        toast.warning("Booking saved, but the email notification is delayed — we'll still see your request.");
      }
      setForm({ name: "", phone: "", email: "", service: "", date: "", time: "", address: "", payment: "", notes: "" });
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("That slot was just taken — please pick another time.");
        setForm((f) => ({ ...f, time: "" }));
      } else {
        toast.error("Could not send your booking. Please call us directly.", {
          description: BUSINESS.phone,
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="relative py-20 sm:py-28 lg:py-36 noise-overlay">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_80%,rgba(0,240,255,0.06),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-volt mb-4">
            06 / Book Your Service
          </div>
          <h2 className="font-syne font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-100 leading-tight max-w-3xl">
            Lock in <span className="metallic-text">your slot.</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-5 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-2 space-y-5"
          >
            <a
              data-testid="direct-call-link"
              href={`tel:${BUSINESS.phone}`}
              className="group flex items-center gap-5 bg-panel border border-edge rounded-xl p-6 hover:border-volt/60 hover:shadow-[0_0_24px_rgba(0,240,255,0.15)] transition-[border-color,box-shadow] duration-300"
            >
              <span className="w-12 h-12 rounded-full bg-volt/10 border border-volt/40 flex items-center justify-center text-volt">
                <Phone size={18} />
              </span>
              <span>
                <span className="block font-mono2 text-[10px] uppercase tracking-[0.3em] text-slate-500">Call Direct</span>
                <span className="block font-syne font-bold text-xl text-slate-100 group-hover:text-volt transition-colors mt-1">
                  {BUSINESS.phone}
                </span>
              </span>
            </a>
            <a
              data-testid="direct-email-link"
              href={`mailto:${BUSINESS.email}`}
              className="group flex items-center gap-5 bg-panel border border-edge rounded-xl p-6 hover:border-volt/60 hover:shadow-[0_0_24px_rgba(0,240,255,0.15)] transition-[border-color,box-shadow] duration-300"
            >
              <span className="w-12 h-12 rounded-full bg-volt/10 border border-volt/40 flex items-center justify-center text-volt">
                <Mail size={18} />
              </span>
              <span className="min-w-0">
                <span className="block font-mono2 text-[10px] uppercase tracking-[0.3em] text-slate-500">Email Us</span>
                <span className="block font-syne font-bold text-base sm:text-lg text-slate-100 group-hover:text-volt transition-colors mt-1 break-all">
                  {BUSINESS.email}
                </span>
              </span>
            </a>
            <div className="flex items-center gap-5 bg-panel border border-edge rounded-xl p-6">
              <span className="w-12 h-12 rounded-full bg-volt/10 border border-volt/40 flex items-center justify-center text-volt">
                <Truck size={18} />
              </span>
              <span>
                <span className="block font-mono2 text-[10px] uppercase tracking-[0.3em] text-slate-500">Fully Mobile Service</span>
                <span className="block font-syne font-bold text-lg text-slate-100 mt-1">Wanneroo to Serpentine — we come to you</span>
                <span className="block font-mono2 text-[10px] uppercase tracking-widest text-slate-500 mt-1">Mon – Sat · By appointment</span>
              </span>
            </div>
          </motion.div>

          <motion.form
            data-testid="booking-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-3 bg-panel border border-edge rounded-2xl p-6 sm:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="input-name" className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                  Your Name *
                </label>
                <input
                  id="input-name"
                  data-testid="input-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Full name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="input-phone" className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                  Phone Number *
                </label>
                <input
                  id="input-phone"
                  data-testid="input-phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="04xx xxx xxx"
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label htmlFor="input-email" className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                Email <span className="text-slate-600">(optional — get an instant booking confirmation)</span>
              </label>
              <input
                id="input-email"
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@email.com"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="input-address" className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                Address — Where do we come to? *
              </label>
              <input
                id="input-address"
                data-testid="input-address"
                required
                value={form.address}
                onChange={set("address")}
                placeholder="Street address & suburb"
                className={inputCls}
              />
            </div>

            <div>
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                Select Service *
              </label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger data-testid="select-service" className="bg-carbon border-edge text-slate-100 h-12">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent className="bg-panel border-edge max-h-72">
                  {ALL_SERVICES.map((s) => (
                    <SelectItem
                      key={s.name}
                      value={s.name}
                      data-testid={`select-service-option-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="text-slate-200 focus:bg-volt/10 focus:text-volt"
                    >
                      {s.name} — ${s.price} + GST
                    </SelectItem>
                  ))}
                  <SelectItem value={CUSTOM_OPTION} data-testid="select-service-option-custom" className="text-slate-200 focus:bg-volt/10 focus:text-volt">
                    Custom Estimate (multi-service)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                Preferred Date &amp; Time *
              </label>
              <AvailabilityPicker
                date={form.date}
                time={form.time}
                onChange={(d, t) => setForm((f) => ({ ...f, date: d, time: t }))}
              />
            </div>

            <div>
              <label htmlFor="input-notes" className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                Notes (optional)
              </label>
              <textarea
                id="input-notes"
                data-testid="input-notes"
                rows={3}
                value={form.notes}
                onChange={set("notes")}
                placeholder="Vehicle make/model, finish preferences, anything we should know…"
                className={`${inputCls} resize-none`}
              />
            </div>

            <div>
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                Payment Method *
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["cash", "Cash"],
                  ["transfer", "Bank Transfer"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`payment-${value}`}
                    onClick={() => setForm((f) => ({ ...f, payment: value }))}
                    className={`rounded-lg border px-4 py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
                      form.payment === value
                        ? "bg-volt text-carbon border-volt font-bold"
                        : "border-edge text-slate-300 hover:border-volt hover:text-volt"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="mt-2 font-mono2 text-[9px] uppercase tracking-widest text-slate-600">
                Pay on the day — cash or bank transfer
              </p>
            </div>

            <button
              data-testid="booking-submit-btn"
              type="submit"
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 bg-volt text-carbon font-syne font-bold text-sm uppercase tracking-widest rounded-full px-6 py-4 hover:bg-white hover:text-carbon disabled:opacity-60 transition-colors duration-300"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Sending…" : "Send Booking Request"}
            </button>
            <p className="text-center font-mono2 text-[10px] uppercase tracking-widest text-slate-600">
              Your request goes straight to the Elite Rims &amp; Trims inbox
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default BookingContactSection;
